<template>
  <div class="azure-dev-ops-form">
    <m-content
      v-if="!fetchLoading && hasError"
      :padding-top="0"
      :padding-x="11"
      :padding-bottom="2"
    >
      <data-source-query-error
        :data-source="dataSource"
        :error="error"
      />
    </m-content>
    <template v-if="!hasConnectionError">
      <full-screen-spinner
        v-if="prefetchLoading"
        :height="100"
      />
      <m-content
        v-else
        :padding-top="0"
        :padding-x="11"
        :padding-bottom="2"
      >
        <div :class="['_grid']">
          <m-form-item
            :label="$t('azureDevOpsForm.selectOrganizationLabel')"
            :style="{ margin: 0 }"
          >
            <m-select
              v-model:value="localQuery.organizationId"
              :items="dataSource[customDatasourceProperty.azureDevOpsOrganizations]"
              value-key="id"
              item-text="name"
              full-width
              match-trigger-width
              @change="changeOrganization"
            />
          </m-form-item>
          <template
            v-if="selectedOrganization !== null && dataSource[customDatasourceProperty.azureDevOpsOrganizations].length > 0"
          >
            <m-form-item
              :label="$t('azureDevOpsForm.selectProjectLabel')"
              :style="{ margin: 0 }"
            >
              <m-select
                v-model:value="localQuery.projectId"
                :items="dataSource[customDatasourceProperty.azureDevOpsProjects]"
                :loading="getProjectsLoading"
                value-key="id"
                item-text="name"
                full-width
                match-trigger-width
                show-search
                @change="changeProject"
              />
            </m-form-item>
          </template>
        </div>
        <m-section
          v-if="selectedProject !== null && dataSource[customDatasourceProperty.azureDevOpsProjects].length > 0"
          heading-size="h5"
          :title="$t('azureDevOpsForm.filter')"
        >
          <template #after-title>
            <m-tooltip :style="{ display: 'inline-flex', paddingLeft: '.8rem' }">
              <m-icon
                type="info-circle"
                :color="$colors.grey.base"
              />
              <template #title>
                {{ $t('azureDevOpsForm.filterInfo') }}
              </template>
            </m-tooltip>
          </template>
          <m-form-item
            :label="$t('azureDevOpsForm.filterModeLabel')"
          >
            <m-radio-group
              :value="localQuery.filterMode"
              :options="filterModes"
              inline
              @change="changeFilterMode"
            />
          </m-form-item>
          <template v-if="localQuery.filterMode === azureDevOpsFilterMode.sharedQueries">
            <m-form-item :label="$t('azureDevOpsForm.selectSharedQueryLabel')">
              <m-select
                v-model:value="localQuery.sharedQueryId"
                :items="dataSource[customDatasourceProperty.azureDevOpsSharedQueries]"
                :loading="getSharedQueriesLoading"
                value-key="id"
                item-text="name"
                full-width
                match-trigger-width
                show-search
                @change="changeSharedQuery"
              />
              <div
                v-if="selectedSharedQuery !== null"
                class="_input-description"
              >
                <template v-if="!getWorkItemsLoading">
                  <m-link
                    :href="selectedSharedQuery.url"
                    target="_blank"
                    class="_link"
                    underline-on-hover
                    :disabled="selectedSharedQuery.url === ''"
                  >
                    <span class="_text">
                      {{ $t('azureDevOpsForm.sharedQueryWorkItemsHint', { count: dataSource[customDatasourceProperty.azureDevOpsWorkItems].length}) }}
                    </span>
                    <m-icon
                      v-if="selectedSharedQuery.url !== ''"
                      type="external-link"
                      size="10"
                    />
                  </m-link>
                </template>
                <template v-else>
                  <m-icon
                    :type="'loading'"
                    size="10"
                    spin
                  />
                  {{ $t('general.loading') }}...
                </template>
              </div>
            </m-form-item>
          </template>
          <template v-if="localQuery.filterMode === azureDevOpsFilterMode.workItems">
            <m-tooltip placement="left">
              <m-form-item
                :label="$t('azureDevOpsForm.selectFilterWorkItemTypeLabel')"
              >
                <m-select
                  v-model:value="localQuery.filterWorkItemType"
                  :items="workItemTypes"
                  :loading="getProjectsLoading"
                  value-key="name"
                  item-text="name"
                  full-width
                  match-trigger-width
                  show-search
                  @change="changeFilterWorkItemType"
                />
              </m-form-item>
              <template #title>
                {{ $t('azureDevOpsForm.selectFilterWorkItemTypeTooltip') }}
              </template>
            </m-tooltip>
            <m-tooltip
              v-if="selectedFilterWorkItemType !== null"
              placement="left"
            >
              <m-form-item
                :label="$t('azureDevOpsForm.selectWorkItemsLabel')"
              >
                <m-select
                  v-model:value="localQuery.filterWorkItemIDs"
                  :items="dataSource[customDatasourceProperty.azureDevOpsWorkItems]"
                  :loading="getWorkItemsLoading"
                  :placeholder="$t('general.all')"
                  value-key="id"
                  item-text="name"
                  full-width
                  match-trigger-width
                  show-search
                  multiple
                  @change="changeFilterWorkItems"
                />
              </m-form-item>
              <template #title>
                {{ $t('azureDevOpsForm.selectWorkItemsTooltip') }}
              </template>
            </m-tooltip>
          </template>
          <m-tooltip placement="left">
            <m-form-item
              :label="$t('azureDevOpsForm.selectAggregationWorkItemTypeLabel')"
            >
              <m-select
                v-model:value="localQuery.aggregationWorkItemType"
                :items="aggregationWorkItemTypes"
                :loading="getWorkItemsLoading"
                :placeholder="$t('general.any')"
                value-key="name"
                item-text="name"
                full-width
                match-trigger-width
                show-search
                clearable
                @change="changeAggregationWorkItemType"
              >
                <template #item-tooltip="{ item }">
                  {{ $t(item.item.tooltip) }}
                </template>
              </m-select>
            </m-form-item>
            <template #title>
              {{ $t('azureDevOpsForm.selectAggregationWorkItemTypeTooltip') }}
            </template>
          </m-tooltip>
        </m-section>
        <m-section
          v-if="isFilterValid"
          heading-size="h5"
          :title="$t('azureDevOpsForm.aggregation')"
        >
          <template #after-title>
            <m-tooltip :style="{ display: 'inline-flex', paddingLeft: '.8rem' }">
              <m-icon
                type="info-circle"
                :color="$colors.grey.base"
              />
              <template #title>
                {{ $t('azureDevOpsForm.aggregationInfo') }}
              </template>
            </m-tooltip>
          </template>
          <m-form-item :label="$t('azureDevOpsForm.selectAggregationWorkItemFieldLabel')">
            <div class="_grid">
              <m-radio-group
                :value="localQuery.aggregationMode"
                :options="aggregationModes"
                inline
                @change="changeAggregationMode"
              />
              <m-select
                v-model:value="localQuery.aggregationWorkItemFieldId"
                :items="aggregationWorkItemFields"
                :placeholder="$t('azureDevOpsForm.aggregationWorkItemFieldPlaceholder')"
                value-key="referenceName"
                item-text="name"
                full-width
                has-tooltip
                tooltip-placement="right"
                match-trigger-width
                show-search
                @change="changeAggregationWorkItemField"
              >
                <template #item-tooltip="{ item }">
                  {{ $t(`azureDevOps.aggregationMode.${camelCase(localQuery.aggregationMode)}.description.${replaceDotsWithUnderscores(item.value)}`) }}
                </template>
              </m-select>
            </div>
          </m-form-item>
        </m-section>
        <m-form-item
          v-if="selectedOrganization !== null && selectedProject !== null && isFilterValid"
          :style="{ marginTop: '2rem' }"
        >
          <always-sync-checkbox v-model:value="localQuery.alwaysSync" />
        </m-form-item>
      </m-content>
      <m-divider xs />
      <template v-if="localQuery.uid === undefined">
        <m-content padding-small>
          <div class="_actions">
            <div class="_btns">
              <m-btn
                class="_btn"
                @click="$emit('close')"
              >
                {{ $t('general.cancel') }}
              </m-btn>
              <m-btn
                color="primary"
                class="_btn"
                :loading="mutateLoading"
                :disabled="!isValid"
                @click="save"
              >
                {{ $t('general.save') }}
              </m-btn>
            </div>
          </div>
        </m-content>
      </template>
      <template v-else>
        <m-card-item
          icon="save"
          :loading="mutateLoading"
          :padding-x="8"
          :disabled="!isValid"
          @click="save"
        >
          {{ $t('general.save') }}
        </m-card-item>
        <m-card-item
          icon="sync"
          :loading="syncLoading"
          :padding-x="8"
          :disabled="!isValid || hasError"
          @click="syncNow"
        >
          {{ $t('dataSource.syncNow') }}
        </m-card-item>
        <m-card-item
          icon="delete"
          :padding-x="8"
          :loading="deleteLoading"
          @click="deleteAzureDevOpsQuery"
        >
          {{ $t('general.delete') }}
        </m-card-item>
      </template>
    </template>
  </div>
</template>

<script setup>
import AlwaysSyncCheckbox from '@/components/datasource/AlwaysSyncCheckbox.vue';
import DataSourceQueryError from '@/components/datasource/DataSourceQueryError.vue';
import FullScreenSpinner from 'shared/components/FullScreenSpinner.vue';
import useAzureDevOps from '@/composables/integrations/azure-dev-ops/azure-dev-ops';
import useConfirmDialog from '@/composables/confirm-dialog';
import useSnackBar from '@/composables/snackbar';
import { azureDevOpsAggregationMode, azureDevOpsFilterMode, customDatasourceProperty, dataSourceQueryGenericError } from 'shared/constants.json';
import { camelCase } from 'lodash-es';
import { computed, ref, watch } from 'vue';
import { copy } from 'shared/lib/copy';
import { isNullOrUndefined } from 'shared/lib/object/object';
import { logCatch } from '@/lib/logger/logger';
import { treeToArray } from '@/lib/tree/tree';
import { unique } from 'shared/lib/array/array';
import { useI18n } from 'vue-i18n';

const snackBar = useSnackBar();
const { t } = useI18n();

const props = defineProps({
  dataSource: {
    type: Object,
    required: true,
  },
  query: {
    type: Object,
    default: () => null,
  },
  goal: {
    type: Object,
    required: true,
  },
});
const emit = defineEmits(['close', 'deleted']);

const {
  createQuery,
  createQueryLoading,
  updateQuery,
  updateQueryLoading,
  deleteQuery,
  deleteQueryLoading: deleteLoading,
  getProjectsLoading,
  getProjects,
  getProject,
  getSharedQueriesLoading,
  getSharedQueries,
  getWorkItemsLoading,
  getWorkItemsBySharedQuery,
  getWorkItemsByType,
} = useAzureDevOps();
const fetchLoading = computed(() => getProjectsLoading.value || getSharedQueriesLoading.value || getWorkItemsLoading.value);
const mutateLoading = computed(() => createQueryLoading.value || updateQueryLoading.value);

const prefetchLoading = ref(false);
const prefetch = () => {
  const prefetches = [];
  const organization = localQuery.value.organizationId === null ? null : { name: localQuery.value.organizationName };
  const project = localQuery.value.projectId === null ? null : { id: localQuery.value.projectId };
  const sharedQuery = localQuery.value.sharedQueryId === null ? null : { id: localQuery.value.sharedQueryId };
  const filterWorkItemType = localQuery.value.filterWorkItemType === null ? null : { name: localQuery.value.filterWorkItemType };
  if (organization === null) {
    return;
  }
  prefetches.push(getProjects(props.dataSource, organization));
  if (project === null) {
    return;
  }
  prefetches.push(getProject(props.dataSource, organization, project));
  prefetches.push(getSharedQueries(props.dataSource, organization, project));
  if (sharedQuery !== null) {
    prefetches.push(getWorkItemsBySharedQuery(props.dataSource, organization, project, sharedQuery));
  }
  if (filterWorkItemType !== null) {
    prefetches.push(getWorkItemsByType(props.dataSource, organization, project, filterWorkItemType));
  }
  prefetchLoading.value = true;
  Promise.all(prefetches).catch(() => {
    snackBar.error();
  }).finally(() => { prefetchLoading.value = false; });
};

const syncLoading = ref(false);
const defaultAzureDevOpsQueryFilter = () => ({
  filterMode: azureDevOpsFilterMode.sharedQueries,
  sharedQueryId: null,
  sharedQueryName: null,
  sharedQueryUrl: null,
  filterWorkItemType: null,
  filterWorkItemIDs: [],
  filterWorkItemNames: [],
});
const defaultAzureDevOpsQueryAggregation = () => ({
  aggregationWorkItemType: null,
  aggregationMode: azureDevOpsAggregationMode.absolute,
  aggregationWorkItemFieldId: 'System.Id',
});
const defaultAzureDevOpsQuery = () => ({
  organizationId: null,
  organizationName: null,
  projectId: null,
  projectName: null,
  projectUrl: null,
  ...defaultAzureDevOpsQueryFilter(),
  ...defaultAzureDevOpsQueryAggregation(),
});
const localQuery = ref(defaultAzureDevOpsQuery());
if (props.query !== null) {
  localQuery.value = copy(props.query);
  prefetch();
}

const isDirty = ref(false);

const hasConnectionError = computed(() => [dataSourceQueryGenericError.incompleteSetup, dataSourceQueryGenericError.unauthenticated].includes(props.dataSource[customDatasourceProperty.dataSourceError]));
const hasDataSourceError = computed(() => !isNullOrUndefined(props.dataSource[customDatasourceProperty.dataSourceError]));
const hasQueryError = computed(() => !isDirty.value && !isNullOrUndefined(props.query?.error));
const hasError = computed(() => hasDataSourceError.value || hasQueryError.value);
const error = computed(() => {
  if (hasDataSourceError.value) {
    return props.dataSource[customDatasourceProperty.dataSourceError];
  }
  if (hasQueryError.value) {
    return props.query?.error;
  }
  return null;
});

const selectedOrganization = computed(() => {
  if (localQuery.value.organizationId === null) {
    return null;
  }

  const el = props.dataSource[customDatasourceProperty.azureDevOpsOrganizations].find((r) => r.id === localQuery.value.organizationId);
  if (el === undefined) {
    return null;
  }

  return el;
});

const changeOrganization = () => {
  localQuery.value = {
    ...localQuery.value,
    ...defaultAzureDevOpsQuery(),
    organizationId: localQuery.value.organizationId,
  };
  isDirty.value = true;
};

watch(selectedOrganization, (newValue, oldValue) => {
  if (prefetchLoading.value) {
    return;
  }
  if (isNullOrUndefined(newValue)) {
    return;
  }
  if (!isNullOrUndefined(oldValue) && newValue.id === oldValue.id) {
    return;
  }
  getProjects(props.dataSource, newValue).catch(() => {
    snackBar.error();
  });
}, { immediate: true });

const selectedProject = computed(() => {
  if (localQuery.value.projectId === null) {
    return null;
  }

  const el = props.dataSource[customDatasourceProperty.azureDevOpsProjects].find((r) => r.id === localQuery.value.projectId);
  if (el === undefined) {
    return null;
  }

  return el;
});

const changeProject = () => {
  localQuery.value = {
    ...localQuery.value,
    ...defaultAzureDevOpsQuery(),
    organizationId: localQuery.value.organizationId,
    projectId: localQuery.value.projectId,
  };
  isDirty.value = true;
};

watch(selectedProject, (newValue, oldValue) => {
  if (prefetchLoading.value) {
    return;
  }
  if (isNullOrUndefined(newValue)) {
    return;
  }
  if (!isNullOrUndefined(oldValue) && newValue.id === oldValue.id) {
    return;
  }
  getProject(props.dataSource, selectedOrganization.value, newValue);
  getSharedQueries(props.dataSource, selectedOrganization.value, newValue).catch(() => {
    snackBar.error();
  });
}, { immediate: true });

const filterModes = azureDevOpsFilterMode.all.map((mode) => ({
  value: mode,
  label: t(`azureDevOps.filterMode.${camelCase(mode)}.label`),
  tooltip: t(`azureDevOps.filterMode.${camelCase(mode)}.description`),
}));

const changeFilterMode = (value) => {
  localQuery.value = {
    ...localQuery.value,
    ...defaultAzureDevOpsQueryFilter(),
    filterMode: value,
  };
  isDirty.value = true;
};

const selectedSharedQuery = computed(() => {
  if (localQuery.value.sharedQueryId === null) {
    return null;
  }

  const el = props.dataSource[customDatasourceProperty.azureDevOpsSharedQueries]?.find((r) => r.id === localQuery.value.sharedQueryId);
  if (el === undefined) {
    return null;
  }

  return el;
});

const changeSharedQuery = () => {
  localQuery.value = {
    ...localQuery.value,
    ...defaultAzureDevOpsQueryFilter(),
    filterMode: localQuery.value.filterMode,
    sharedQueryId: localQuery.value.sharedQueryId,
  };
  isDirty.value = true;
};

watch(selectedSharedQuery, (newValue, oldValue) => {
  if (prefetchLoading.value) {
    return;
  }
  if (isNullOrUndefined(newValue)) {
    return;
  }
  if (!isNullOrUndefined(oldValue) && newValue.id === oldValue.id) {
    return;
  }
  getWorkItemsBySharedQuery(props.dataSource, selectedOrganization.value, selectedProject.value, newValue).catch(() => {
    snackBar.error();
  });
}, { immediate: true });

const workItemTypes = computed(() => selectedProject.value?.workItemTypes ?? []);

const selectedFilterWorkItemType = computed(() => {
  if (localQuery.value.filterWorkItemType === null) {
    return null;
  }

  const el = workItemTypes.value?.find((r) => r.name === localQuery.value.filterWorkItemType);
  if (el === undefined) {
    return null;
  }

  return el;
});

const changeFilterWorkItemType = () => {
  localQuery.value = {
    ...localQuery.value,
    ...defaultAzureDevOpsQueryFilter(),
    filterMode: localQuery.value.filterMode,
    filterWorkItemType: localQuery.value.filterWorkItemType,
  };
  isDirty.value = true;
};

watch(selectedFilterWorkItemType, (newValue, oldValue) => {
  if (prefetchLoading.value) {
    return;
  }
  if (isNullOrUndefined(newValue)) {
    return;
  }
  if (!isNullOrUndefined(oldValue) && newValue.name === oldValue.name) {
    return;
  }
  getWorkItemsByType(props.dataSource, selectedOrganization.value, selectedProject.value, newValue).catch(() => {
    snackBar.error();
  });
}, { immediate: true });

const selectedFilterWorkItems = computed(() => {
  if (localQuery.value.filterWorkItemIDs.length === 0) {
    return [];
  }
  const els = props.dataSource[customDatasourceProperty.azureDevOpsWorkItems]?.filter((e) => localQuery.value.filterWorkItemIDs.includes(e.id));
  if (els === undefined) {
    return [];
  }

  return els;
});
const changeFilterWorkItems = () => {
  isDirty.value = true;
};

const aggregationWorkItemTypes = computed(() => {
  let selectedWorkItems = props.dataSource[customDatasourceProperty.azureDevOpsWorkItems] ?? [];
  if (localQuery.value.filterMode === azureDevOpsFilterMode.workItems) {
    if (selectedFilterWorkItems.value.length !== 0) {
      selectedWorkItems = selectedFilterWorkItems.value;
    }
    selectedWorkItems = treeToArray(selectedWorkItems);
  }
  const selectedWorkItemsTypes = unique(selectedWorkItems.map((wi) => wi.type));
  return workItemTypes.value.filter((type) => selectedWorkItemsTypes.includes(type.name));
});
const selectedAggregationWorkItemType = computed(() => {
  if (localQuery.value.aggregationWorkItemType === null) {
    return null;
  }

  const el = aggregationWorkItemTypes.value.find((e) => e.name === localQuery.value.aggregationWorkItemType);
  if (el === undefined) {
    return null;
  }

  return el;
});
const changeAggregationWorkItemType = () => {
  localQuery.value = {
    ...localQuery.value,
    ...defaultAzureDevOpsQueryAggregation(),
    aggregationWorkItemType: localQuery.value.aggregationWorkItemType,
  };
  isDirty.value = true;
};

const aggregationModes = azureDevOpsAggregationMode.all.map((mode) => ({
  value: mode,
  label: t(`azureDevOps.aggregationMode.${camelCase(mode)}.label`),
  tooltip: t(`azureDevOps.aggregationMode.${camelCase(mode)}.info`),
}));
const changeAggregationMode = (value) => {
  localQuery.value.aggregationMode = value;
  isDirty.value = true;

  if (value === azureDevOpsAggregationMode.percentage && localQuery.value.aggregationWorkItemFieldId === 'Microsoft.VSTS.Scheduling.RemainingWork') {
    localQuery.value.aggregationWorkItemFieldId = null;
  }
};

const replaceDotsWithUnderscores = (str) => str.replace(/\./g, '_');

const aggregationWorkItemFields = computed(() => {
  let fields;
  if (!isNullOrUndefined(selectedAggregationWorkItemType.value)) {
    fields = selectedAggregationWorkItemType.value.fields;
  } else {
    const getCommonFields = (workItemTypes) => {
      if (!workItemTypes.length) return [];
      return workItemTypes.slice(1).reduce((acc, item) => {
        const currentSet = new Set(item.fields.map((f) => f.referenceName));
        return acc.filter((f) => currentSet.has(f.referenceName));
      }, workItemTypes[0].fields);
    };
    fields = getCommonFields(aggregationWorkItemTypes.value);
  }

  return fields
    .filter((field) => !(localQuery.value.aggregationMode === azureDevOpsAggregationMode.percentage && field.referenceName === 'Microsoft.VSTS.Scheduling.RemainingWork'))
    .map((field) => {
      if (field.referenceName === 'System.Id') {
        return { ...field, name: t(`azureDevOps.aggregationMode.${camelCase(localQuery.value.aggregationMode)}.fields.${replaceDotsWithUnderscores(field.referenceName)}`) };
      }
      return field;
    });
});

const selectedAggregationWorkItemField = computed(() => {
  if (localQuery.value.aggregationWorkItemFieldId === null) {
    return null;
  }
  const el = aggregationWorkItemFields.value.find((e) => e.referenceName === localQuery.value.aggregationWorkItemFieldId);
  if (el === undefined) {
    return null;
  }
  return el;
});

const changeAggregationWorkItemField = (value) => {
  localQuery.value.aggregationWorkItemFieldId = value;
  isDirty.value = true;
};

const isFilterValid = computed(() => (localQuery.value.filterMode === azureDevOpsFilterMode.sharedQueries && selectedSharedQuery.value !== null)
    || (localQuery.value.filterMode === azureDevOpsFilterMode.workItems && selectedFilterWorkItemType.value !== null));
const isAggregationValid = computed(() => selectedAggregationWorkItemField.value !== null);
const isValid = computed(() => selectedOrganization.value !== null
    && selectedProject.value !== null
    && isFilterValid.value
    && isAggregationValid.value);

const save = () => {
  if (localQuery.value.uid === undefined) {
    createAzureDevOpsQuery(localQuery.value);
    return;
  }

  updateAzureDevOpsQuery(localQuery.value);
};
const buildFilter = () => {
  switch (localQuery.value.filterMode) {
    case azureDevOpsFilterMode.sharedQueries:
      return { sharedQueryName: selectedSharedQuery.value.name, sharedQueryUrl: selectedSharedQuery.value.url };
    case azureDevOpsFilterMode.workItems:
      return { filterWorkItemNames: selectedFilterWorkItems.value.map((wi) => wi.name) };
    default:
      return {};
  }
};
const createAzureDevOpsQuery = (query) => {
  createQuery({
    ...query,
    dataSource: { uid: props.dataSource.uid },
    goal: { uid: props.goal.uid },
    organizationName: selectedOrganization.value.name,
    projectName: selectedProject.value.name,
    projectUrl: selectedProject.value.url,
    ...buildFilter(),
    aggregationWorkItemType: selectedAggregationWorkItemType.value?.name ?? null,
  }).then((query) => {
    localQuery.value = copy(query);
    isDirty.value = false;
  }).catch(logCatch(() => {
    snackBar.error();
  }));
};
const updateAzureDevOpsQuery = (query) => {
  updateQuery({
    ...query,
    dataSource: { uid: props.dataSource.uid },
    organizationName: selectedOrganization.value.name,
    projectName: selectedProject.value.name,
    projectUrl: selectedProject.value.url,
    ...buildFilter(),
    aggregationWorkItemType: selectedAggregationWorkItemType.value?.name ?? null,
  }).then((query) => {
    localQuery.value = copy(query);
    isDirty.value = false;
  }).catch(logCatch(() => {
    snackBar.error();
  }));
};
const syncNow = () => {
  syncLoading.value = true;
  updateQuery(props.goal.azureDevOpsQuery).then((query) => {
    localQuery.value = copy(query);
    isDirty.value = false;
    snackBar.success(t('dataSource.successfullySynced'));
  }).catch(logCatch(() => {
    snackBar.error();
  })).finally(() => {
    syncLoading.value = false;
  });
};

const confirmDialog = useConfirmDialog();
const deleteAzureDevOpsQuery = () => {
  const deleteMethod = () => deleteQuery(props.goal.azureDevOpsQuery.uid).then(() => {
    emit('deleted');
    emit('close');
  }).catch(() => {
    snackBar.error();
  });

  confirmDialog.confirm({
    title: t('dataSource.deleteConnectionPrompt'),
    onOk() {
      deleteMethod();
    },
  });
};

</script>

<style scoped lang="scss" type="text/scss">
.azure-dev-ops-form {
  ._grid {
    display: grid;
    align-items: center;
    gap: 1rem;
  }

  .m-section {
    margin-top: 2rem;
  }

  .m-form-item {
    margin-bottom: 1.4rem;
  }

  ._input-description {
    display: flex;
    gap: .4rem;
    margin-bottom: 1.2rem;
    font-size: $font-size-2;
    color: $font-color-secondary;
  }

  ._link {
    display: flex;
    color: $font-color-primary;

    ._text {
      margin-right: .4rem;
    }
  }

  ._actions {
    display: flex;

    ._btns {
      display: flex;
      margin-left: auto;

      ._btn {
        margin-left: .4rem;
      }
    }
  }
}
</style>
